import React, { useContext, useEffect, useRef, useState } from "react";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { dateFormatter, fromGenericBeToGenericFe, useActionColDef } from "../../utils";
import { DEFAULT_COL_DEF, SET_SNACKBAR } from "../../constants";
import { useRecoilState } from "recoil";
import SectionHeader from "../../../../util/component/SectionHeader";
import { Box, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import DialogComponent from "../../common/components/DialogComponent";
import { useAgGridLocale } from "../../../../config/globalConfig";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import dayjs from "dayjs";
import { AncillaryEntityResponse, FormSearchAncillaryModel } from "../../api/types/ancillary";
import Loading from "../../common/components/Loading";
import { useAncillaryRepository } from "../../api/repositories/AncillaryRepository";
import AncillaryFilters from "./Filters/AncillaryFilters";
import AncillaryContainerView from "../Ancillary/AncillaryContainerView";
import { DialogInterface, SelectElementModelBE, SelectElementModelFE } from "../../api/types/generic";
import { useConstantsRepository } from "../../api/repositories/ConstantsRepository";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import ActionsPopover from "../../common/components/ActionsPopover";
import BasicToolbar from "../../common/components/BasicToolbar";
import { useCommercialSegmentRepository } from "../../api/repositories/CommercialSegmentRepository";
import { commercialSegmentListAtom } from "../../state/commercialSegmentsAtom";
import useAgGridCustomHook from "../../hooks/useAgGridCustomHook";
import Chip from "@mui/material/Chip";

const Ancillaries = () => {
  // Use Translation
  const { t, i18n } = useTranslation(["translation", "pricing"]);

  // Use Context
  const { dispatchGlobalData, globalData: { user } } = useContext(GlobalContext);

  // Use AgGrid Locale
  const agGridLocale = useAgGridLocale();

  // Use Ref
  const gridRef = useRef<AgGridReact>(null);

  // Use Recoil
  const [, setCommercialSegmentList] = useRecoilState<SelectElementModelFE[]>(
    commercialSegmentListAtom
  );

  // Use Repository Hooks
  const {
    getAncillariesPaged,
    deleteAncillary,
    terminateAncillary
  } = useAncillaryRepository();
  const { getTypes } = useConstantsRepository();
  const { getCommercialSegments } = useCommercialSegmentRepository();

  // Use State
  const [viewAncillary, setViewAncillary] = useState<boolean>(false);
  const [rowIndex, setRowIndex] = useState<number | null>(null);
  const [dialog, setDialog] = useState<DialogInterface | null>(null);
  const [types, setTypes] = useState<string[]>([]);

  const handleGetStaticContent = async () => {
    const types = await getTypes();
    setTypes(types);

    // TODO: togliere COMMERCIAL_SEGMENTS || quando si pusha
    // const commercialSegmentsData = COMMERCIAL_SEGMENTS || await getCommercialSegments();
    const commercialSegmentsData = await getCommercialSegments();

    const commercialSegmentList: SelectElementModelFE[] =
      fromGenericBeToGenericFe(commercialSegmentsData);
    setCommercialSegmentList(commercialSegmentList);
  };
  //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
  const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
    (el: SelectElementModelBE) => ({
      code: el.name,
      label: t("translation:legalEntities.".concat(el.name))
    })
  );

  //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
  const optionsFiltered = legalEntityOptions
    .filter(function(el) {
      return el.code !== "all";
    });

  const everyLegalEntitiesWithoutAll = legalEntityOptions
    .map((el) => el.code as string)
    .filter((el) => el !== "all");

  //setto tutti i dealers options
  const dealerOption: SelectElementModelFE[] = user.dealers?.map(
    (el: SelectElementModelBE) => ({
      code: el.name,
      label: t("translation:dealers.".concat(el.name))
    })
  );

  const dealerFilteredOptions = dealerOption
    .map((el) => el.code as string)
    .filter((el) => el !== "all");

  const [testFilters, setTestFilters] = useState<FormSearchAncillaryModel>({
    ancillaryCode: "",
    commercialCategoryId: null,
    commercialSegmentId: null,
    ancillaryType: "",
    pickupDateFrom: "",
    pickupDateTo: "",
    reservationFrom: "",
    reservationTo: "",
    dealers: dealerFilteredOptions,
    legalEntities: everyLegalEntitiesWithoutAll
  });

  // Use Effect
  useEffect(() => {
    handleGetStaticContent();
    // eslint-disable-next-line
  }, []);

  // useEffect(() => {
  //     setTimeout(() => {
  //         handleTriggerFiltersInitial();
  //     }, 500);
  // }, [i18n.resolvedLanguage]);

  /* AG GRID - START */
  // Use Grid Data Custom Hook
  const {
    setServerSideDatasource,
    handleTriggerFilters,
    refresh
  } = useAgGridCustomHook<AncillaryEntityResponse, FormSearchAncillaryModel>({
    gridRef,
    api: getAncillariesPaged,
    initialSortColId: "ancillaryCode",
    entityFilters: testFilters,
    customSort: "AncillaryEntityResponse"
  });

  const handleTriggerFiltersInitial = () => {
    handleTriggerFilters({
      commercialSegmentId: (
        testFilters.commercialSegmentId as SelectElementModelFE
      )?.code as number,
      ancillaryCode: testFilters.ancillaryCode,
      ancillaryType: testFilters.ancillaryType as string,
      pickupDateFrom: testFilters.pickupDateFrom
        ? dayjs(testFilters.pickupDateFrom).toISOString()
        : testFilters.pickupDateFrom,
      pickupDateTo: testFilters.pickupDateTo
        ? dayjs(testFilters.pickupDateTo).toISOString()
        : testFilters.pickupDateTo,
      reservationFrom: testFilters.reservationFrom
        ? dayjs(testFilters.reservationFrom).toISOString()
        : testFilters.reservationFrom,
      reservationTo: testFilters.reservationTo
        ? dayjs(testFilters.reservationTo).toISOString()
        : testFilters.reservationTo,
      showOnlyActive: testFilters.showOnlyActive,
      legalEntities:
        ((testFilters.legalEntities as SelectElementModelFE)
          ?.code as string) === "all"
          ? everyLegalEntitiesWithoutAll
          : ((testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)),
      dealers:
        ((testFilters.dealers as SelectElementModelFE)
          ?.code as string) === "all"
          ? dealerFilteredOptions
          : ((testFilters.dealers as SelectElementModelFE[]).map(el => el.code as string))
    });
  };

  const handleTriggerResetFilters = () => {
    setTestFilters({
      ancillaryCode: "",
      commercialCategoryId: null,
      commercialSegmentId: null,
      ancillaryType: "",
      pickupDateFrom: "",
      pickupDateTo: "",
      reservationFrom: "",
      reservationTo: "",
      dealers: [],
      legalEntities: []
    });
    handleTriggerFilters({
      ancillaryCode: "",
      commercialCategoryId: null,
      commercialSegmentId: null,
      ancillaryType: "",
      pickupDateFrom: "",
      pickupDateTo: "",
      reservationFrom: "",
      reservationTo: "",
      dealers: [],
      legalEntities: []
    });
  };

  useEffect(() => {
    if (gridRef?.current)
      handleTriggerFilters({
        commercialSegmentId: (
          testFilters.commercialSegmentId as SelectElementModelFE
        )?.code as number,
        ancillaryCode: testFilters.ancillaryCode,
        ancillaryType: testFilters.ancillaryType as string,
        pickupDateFrom: testFilters.pickupDateFrom
          ? dayjs(testFilters.pickupDateFrom).toISOString()
          : testFilters.pickupDateFrom,
        pickupDateTo: testFilters.pickupDateTo
          ? dayjs(testFilters.pickupDateTo).toISOString()
          : testFilters.pickupDateTo,
        reservationFrom: testFilters.reservationFrom
          ? dayjs(testFilters.reservationFrom).toISOString()
          : testFilters.reservationFrom,
        reservationTo: testFilters.reservationTo
          ? dayjs(testFilters.reservationTo).toISOString()
          : testFilters.reservationTo,
        showOnlyActive: testFilters.showOnlyActive,
        legalEntities:
          ((testFilters.legalEntities as SelectElementModelFE)
            ?.code as string) === "all"
            ? everyLegalEntitiesWithoutAll
            : ((testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)),
        dealers:
          ((testFilters.dealers as SelectElementModelFE)
            ?.code as string) === "all"
            ? dealerFilteredOptions
            : ((testFilters.dealers as SelectElementModelFE[]).map(el => el.code as string))
      });
  }, [testFilters.showOnlyActive]);

  useEffect(() => {
    handleTriggerResetFilters();
  }, []);

  const { actionColDef } = useActionColDef();
  const columnDefs: ColDef<AncillaryEntityResponse>[] = [
    {
      field: "ancillaryCode",
      headerName: t("pricing:ancillary.form.labels.code"),
      headerTooltip: t("pricing:ancillary.form.labels.code"),
      flex: 1,
      filter: "text",
      cellRenderer: (params: ICellRendererParams) => params.data.code
    },
    {
      field: "legalEntities",
      headerName: t("pricing:ancillary.form.labels.legalEntity"),
      headerTooltip: t("pricing:ancillary.form.labels.legalEntity"),
      editable: false,
      flex: 1,
      filter: "text",
      cellRenderer: (params: ICellRendererParams) => t("translation:legalEntities.".concat(params.data.legalEntity)),
      hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
    },
    {
      field: "dealers",
      headerName: t("pricing:ancillary.form.labels.dealer"),
      headerTooltip: t("pricing:ancillary.form.labels.dealer"),
      editable: false,
      flex: 1,
      filter: "text",
      cellRenderer: (params: ICellRendererParams) => t("translation:dealers.".concat(params.data.dealer)),
      hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
    },
    {
      field: "segments",
      colId: "commercialSegmentId",
      headerName: t("pricing:ancillary.form.labels.commercialSegment"),
      headerTooltip: t("pricing:ancillary.form.labels.commercialSegment"),
      flex: 1,
      sortable: false,
      cellRenderer: (params: ICellRendererParams) => params.data.segments.map((el: SelectElementModelBE) => el.name).join(", "),
      filter: "text"
    },
    {
      field: "nameAncillary",
      colId: "nameAncillary",
      headerName: t("pricing:ancillary.form.labels.nameAncillary"),
      headerTooltip: t("pricing:ancillary.form.labels.nameAncillary"),
      flex: 1,
      filter: "text",
      cellRenderer: (params: ICellRendererParams) => {
        // LPP-21049: Skip defaultLabel fallback to avoid showing description instead of name
        const multilanguage = params.data?.prcMultilanguage;
        if (multilanguage && multilanguage[i18n.language]) {
          return multilanguage[i18n.language];
        }
        return params.data.nameAncillary;
      }
    },
    {
      field: "reservationFrom",
      headerName: t("pricing:ancillary.form.labels.reservationFrom"),
      headerTooltip: t("pricing:ancillary.form.labels.reservationFrom"),
      flex: 1,
      valueFormatter: (params: any) => params.data.reservationFrom ? dateFormatter(params.data.reservationFrom) : "",
      filter: "text"
    },
    {
      field: "reservationTo",
      headerName: t("pricing:ancillary.form.labels.reservationTo"),
      headerTooltip: t("pricing:ancillary.form.labels.reservationTo"),
      flex: 1,
      valueFormatter: (params: any) => params.data.reservationTo ? dateFormatter(params.data.reservationTo) : "",
      filter: "text"
    },
    {
      field: "pickupDateFrom",
      headerName: t("pricing:ancillary.form.labels.pickupDateFrom"),
      headerTooltip: t("pricing:ancillary.form.labels.pickupDateFrom"),
      flex: 1,
      valueFormatter: (params: any) => params.data.pickupFrom ? dateFormatter(params.data.pickupFrom) : "",
      filter: "text"
    },
    {
      field: "pickupDateTo",
      headerName: t("pricing:ancillary.form.labels.pickupDateTo"),
      headerTooltip: t("pricing:ancillary.form.labels.pickupDateTo"),
      flex: 1,
      valueFormatter: (params: any) => params.data.pickupTo ? dateFormatter(params.data.pickupTo) : "",
      filter: "text"
    },
    {
      field: "ancillaryType",
      hide: true,
      filter: "text"
    },
    {
      field: "isOneWay",
      colId: "isOneWay",
      headerName: "One Way",
      headerTooltip: "One Way",
      flex: 1,
      cellRenderer: (params: any) => {
        return params.data.isOneWay ?
          <Chip label={t("pricing:labels.yes")} sx={{ bgcolor: "#BEE5CA" }} variant={"filled"} />
          :
          null;
      },
      sortable: false
    },
    {
      field: "showOnlyActive",
      filter: "text",
      hide: true
    },
    {
      ...actionColDef,
      cellRenderer: (params: ICellRendererParams) => (
        <ActionsPopover
          url={`../ancillary/ancillary/create/${params.data.id}`}
          params={params}
          view={{
            action: viewButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
              return profile.auths.find((auth: any) => {
                return auth.name === "SCOPE_PRC_VIEW_DETAIL_ANCILLARY";
              });
            })
          }}
          update={!params.data.isOneWay ? {
            action: updateButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
              return profile.auths.find((auth: any) => {
                return auth.name === "SCOPE_PRC_UPDATE_ANCILLARY";
              });
            })
          } : undefined}
          terminate={{
            action: terminateButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
              return profile.auths.find((auth: any) => {
                return auth.name === "SCOPE_PRC_TERMINATE_ANCILLARY";
              });
            })
          }}
          del={!params.data.used ? {
            action: deleteButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
              return profile.auths.find((auth: any) => {
                return auth.name === "SCOPE_PRC_DELETE_ANCILLARY";
              });
            })
          } : undefined}
        />
      )
    }
  ];

  const defaultColDef = DEFAULT_COL_DEF;
  /* AG GRID - END */

  /* TABLE ACTIONS - START */
  const viewButtonClicked = (data: AncillaryEntityResponse) => {
    setViewAncillary(true);
    setRowIndex(data.id);
  };

  const updateButtonClicked = (data: AncillaryEntityResponse) => {
    //navigate("../ancillary/ancillary/create", {replace: true, state: {ancillaryId: data.id}});
  };

  const terminate = async (id: number) => {
    await terminateAncillary(id);

    setDialog(null);
    dispatchGlobalData(SET_SNACKBAR.SUCCESS);
    refresh();
  };

  const terminateButtonClicked = async (data: AncillaryEntityResponse) => {
    setDialog({
      open: true,
      onClickConfirm: () => terminate(data.id),
      message: t("pricing:ancillaries.dialogContents.terminateAncillaryInfo")
    });
  };

  const deleteButtonClicked = (data: AncillaryEntityResponse) => {
    setDialog({
      open: true,
      onClickConfirm: () => handleConfirmDelete(data.id),
      message: t("pricing:ancillaries.dialogContents.removeAncillaryInfo")
    });
  };

  const handleConfirmDelete = async (id: number) => {
    try {
      if (id) {
        await deleteAncillary(id);
        setDialog(null);
        await refresh();
        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
      }
    } catch (e) {
    }
  };
  /* TABLE ACTIONS - END */

  const toolbar = {
    renderActions: () => (
      <BasicToolbar
        addIcon={
          !!user.profiles.find((profile: any) => {
            return profile.auths.find((auth: any) => {
              return auth.name === "SCOPE_PRC_CREATE_ANCILLARY";
            });
          }) ?
            {
              onClick: () => {
              }
            } : undefined}
        activeSwitch={{
          label: t("pricing:rentalPeriods.form.labels.showActive"),
          handleChange: (checked: boolean) =>
            setTestFilters({
              ...testFilters,
              showOnlyActive: checked
            }),
          value: testFilters.showOnlyActive as boolean
        }}
        url={"../ancillary/ancillary/create/new"}
      />
    )
  };

  return (
    <Box>
      <SectionHeader
        title={t("translation:auths.SCOPE_PRC_VIEW_ANCILLARY.name")}
      />
      <AncillaryFilters
        handleClickSearch={handleTriggerFiltersInitial}
        handleClickResetSearch={handleTriggerResetFilters}
        types={types}
        testFilters={testFilters}
        setTestFilters={setTestFilters}
      />

      <Grid container spacing={2} p={2}>
        <Grid item xs={12} sm={12}>
          {viewAncillary && rowIndex ? (
              <AncillaryContainerView
                ancillaryId={rowIndex}
                open={viewAncillary}
                setOpen={setViewAncillary}
              />
            ) :
            <AgGridCustomTable
              gridRef={gridRef}
              toolbar={toolbar}
              defaultColDef={defaultColDef}
              columnDefs={columnDefs}
              pagination
              localeText={agGridLocale}
              tooltipShowDelay={0}
              domLayout="autoHeight"
              rowModelType="serverSide"
              paginationPageSize={10}
              cacheBlockSize={10}
              serverSideInfiniteScroll
              onGridReady={setServerSideDatasource}
            />
          }
          {dialog?.open && (
            <DialogComponent
              type={"info"}
              onClickConfirm={dialog.onClickConfirm}
              setOpen={() => setDialog(null)}
              open={dialog.open}
              message={dialog.message}
            />
          )}
        </Grid>
      </Grid>

      <Loading />
    </Box>
  );
};

export default Ancillaries;
